import { config } from '@/config'
import { createPimlicoClients } from '@/modules/auth/pimlico'
import {
  type StoredPasskeyCredential,
  getStoredCredential,
  setStoredCredential,
} from '@/modules/auth/session'
import { type SmartAccountClient, createSmartAccountClient } from 'permissionless'
import { toKernelSmartAccount } from 'permissionless/accounts'
import type { Hex } from 'viem'
import { http, keccak256 } from 'viem'
import {
  type P256Credential,
  createWebAuthnCredential,
  entryPoint07Address,
  toWebAuthnAccount,
} from 'viem/account-abstraction'

export interface PasskeyIdentity {
  mode: 'passkey'
  username: string
  rpId: string
  credential: StoredPasskeyCredential
  address: Hex
  smartAccountAddress: Hex
  smartAccountClient: SmartAccountClient | null
  pimlicoReady: boolean
}

function ensureBrowserApi(): void {
  if (typeof window === 'undefined') {
    throw new Error('Passkeys are available only in the browser.')
  }
  if (!window.PublicKeyCredential || !window.navigator.credentials) {
    throw new Error('This browser does not support passkeys.')
  }
}

export function resolvePasskeyRpId(): string {
  if (typeof window === 'undefined') return config.passkey.rpId || 'localhost'
  const hostname = window.location.hostname
  const configured = config.passkey.rpId
  if (configured && (hostname === configured || hostname.endsWith(`.${configured}`))) {
    return configured
  }
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'localhost'
  return hostname
}

function decodeCredentialId(id: string): Uint8Array {
  try {
    let base64 = id.replace(/-/g, '+').replace(/_/g, '/')
    const remainder = base64.length % 4
    if (remainder === 2) base64 += '=='
    if (remainder === 3) base64 += '='

    const decoded = atob(base64)
    const bytes = new Uint8Array(decoded.length)
    for (let index = 0; index < decoded.length; index += 1) {
      bytes[index] = decoded.charCodeAt(index)
    }
    return bytes
  } catch {
    const hex = id.startsWith('0x') ? id.slice(2) : id
    const bytes = hex.match(/.{1,2}/g)?.map((byte) => Number.parseInt(byte, 16)) ?? []
    if (bytes.length === 0 || bytes.some((byte) => Number.isNaN(byte))) {
      throw new Error('Unable to decode passkey credential id.')
    }
    return Uint8Array.from(bytes)
  }
}

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  return Uint8Array.from(bytes).buffer
}

function deriveFallbackAddress(credential: StoredPasskeyCredential): Hex {
  const digest = keccak256(credential.publicKey)
  return `0x${digest.slice(-40)}` as Hex
}

async function buildPasskeyIdentity(
  credential: StoredPasskeyCredential,
  promptForAssertion: boolean,
): Promise<PasskeyIdentity> {
  ensureBrowserApi()

  if (promptForAssertion) {
    const assertion = await window.navigator.credentials.get({
      publicKey: {
        challenge: window.crypto.getRandomValues(new Uint8Array(32)),
        rpId: credential.rpId,
        userVerification: 'required',
        allowCredentials: [
          {
            id: toArrayBuffer(decodeCredentialId(credential.id)),
            type: 'public-key',
            transports: ['internal', 'hybrid'],
          },
        ],
        timeout: 60_000,
      },
    })
    if (!assertion) {
      throw new Error('Passkey authentication was cancelled.')
    }
  }

  const fallbackAddress = deriveFallbackAddress(credential)
  const { chain, publicClient, pimlicoClient, bundlerUrl, sponsorshipPolicyId } =
    createPimlicoClients()

  if (!pimlicoClient || !bundlerUrl) {
    return {
      mode: 'passkey',
      username: credential.username,
      rpId: credential.rpId,
      credential,
      address: fallbackAddress,
      smartAccountAddress: fallbackAddress,
      smartAccountClient: null,
      pimlicoReady: false,
    }
  }

  const webAuthnAccount = toWebAuthnAccount({
    credential: {
      id: credential.id,
      publicKey: credential.publicKey,
    },
    rpId: credential.rpId,
  })

  const account = await toKernelSmartAccount({
    client: publicClient as never,
    version: '0.3.1',
    owners: [webAuthnAccount],
    entryPoint: {
      address: entryPoint07Address,
      version: '0.7',
    },
  })

  const smartAccountClient = createSmartAccountClient({
    account,
    chain,
    bundlerTransport: http(bundlerUrl),
    paymaster: pimlicoClient as never,
    paymasterContext: sponsorshipPolicyId ? { sponsorshipPolicyId } : undefined,
  })

  return {
    mode: 'passkey',
    username: credential.username,
    rpId: credential.rpId,
    credential,
    address: account.address as Hex,
    smartAccountAddress: account.address as Hex,
    smartAccountClient,
    pimlicoReady: true,
  }
}

function toStoredCredential(
  credential: P256Credential,
  username: string,
  rpId: string,
): StoredPasskeyCredential {
  return {
    id: credential.id,
    publicKey: credential.publicKey,
    rpId,
    username,
    createdAt: new Date().toISOString(),
  }
}

function buildDefaultUsername(): string {
  const suffix = Math.floor(Math.random() * 9_999)
    .toString()
    .padStart(4, '0')
  return `${config.passkey.usernamePrefix}-${suffix}`
}

export async function registerPasskeyIdentity(username?: string): Promise<PasskeyIdentity> {
  ensureBrowserApi()
  const rpId = resolvePasskeyRpId()
  const resolvedUsername = (username ?? '').trim() || buildDefaultUsername()

  const credential = await createWebAuthnCredential({
    name: resolvedUsername,
    rp: {
      id: rpId,
      name: config.passkey.rpName,
    },
  })

  const storedCredential = toStoredCredential(credential, resolvedUsername, rpId)
  setStoredCredential(storedCredential)

  return buildPasskeyIdentity(storedCredential, false)
}

export async function authenticatePasskeyIdentity(): Promise<PasskeyIdentity> {
  const storedCredential = getStoredCredential()
  if (!storedCredential) {
    throw new Error('No saved passkey was found on this device. Create one first.')
  }
  return buildPasskeyIdentity(storedCredential, true)
}

const RESTORE_TIMEOUT_MS = 8_000

export async function restorePasskeyIdentity(): Promise<PasskeyIdentity | null> {
  const storedCredential = getStoredCredential()
  if (!storedCredential) return null

  const fallback: PasskeyIdentity = {
    mode: 'passkey',
    username: storedCredential.username,
    rpId: storedCredential.rpId,
    credential: storedCredential,
    address: deriveFallbackAddress(storedCredential),
    smartAccountAddress: deriveFallbackAddress(storedCredential),
    smartAccountClient: null,
    pimlicoReady: false,
  }

  try {
    const timeout = new Promise<PasskeyIdentity>((resolve) =>
      setTimeout(() => resolve(fallback), RESTORE_TIMEOUT_MS),
    )
    return await Promise.race([buildPasskeyIdentity(storedCredential, false), timeout])
  } catch {
    return fallback
  }
}

export async function isPasskeySupported(): Promise<boolean> {
  if (typeof window === 'undefined' || !window.PublicKeyCredential) return false

  // On iPadOS Safari, isUserVerifyingPlatformAuthenticatorAvailable can return
  // false when no passcode/biometric is configured, or in certain PWA contexts.
  // We also check isConditionalMediationAvailable as a secondary signal — iPadOS 16+
  // supports conditional UI even when platform authenticator detection is flaky.
  if (
    typeof window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable !== 'function'
  ) {
    return true
  }
  try {
    const platformAvailable =
      await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
    if (platformAvailable) return true

    // Fallback: some iPadOS versions report no platform authenticator but still
    // support passkeys via iCloud Keychain synced credentials or security keys.
    if (typeof window.PublicKeyCredential.isConditionalMediationAvailable === 'function') {
      return await window.PublicKeyCredential.isConditionalMediationAvailable()
    }

    return false
  } catch {
    return false
  }
}
